"use client";

import { c as _c } from "react/compiler-runtime";
import NumberFlow from "@number-flow/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { usePie } from "./pie-context";

// NumberFlow format - subset of Intl.NumberFormatOptions
interface NumberFlowFormat {
  notation?: "standard" | "compact";
  compactDisplay?: "short" | "long";
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  minimumIntegerDigits?: number;
  minimumSignificantDigits?: number;
  maximumSignificantDigits?: number;
  style?: "decimal" | "percent" | "currency";
  currency?: string;
  currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  unit?: string;
  unitDisplay?: "short" | "long" | "narrow";
}
export interface PieCenterProps {
  /** Label shown below the value. Default: "Total" when not hovering */
  defaultLabel?: string;
  /** Format options for NumberFlow. Default: standard notation */
  formatOptions?: NumberFlowFormat;
  /** Custom render function for complete control over center content */
  children?: (props: {
    value: number;
    label: string;
    isHovered: boolean;
    data: {
      label: string;
      value: number;
      logo?: string;
      color?: string;
      fill?: string;
    };
  }) => ReactNode;
  /** Additional class name for the container */
  className?: string;
  /** Class name for the value text. Default: "text-2xl font-bold" */
  valueClassName?: string;
  /** Class name for the label text. Default: "text-xs" */
  labelClassName?: string;
  /** Prefix to show before the number (e.g., "$") */
  prefix?: string;
  /** Suffix to show after the number (e.g., "%") */
  suffix?: string;
}

// Default format options
const defaultFormatOptions: NumberFlowFormat = {
  notation: "standard",
  maximumFractionDigits: 0
};

/**
 * PieCenter displays content in the center of a donut/pie chart.
 *
 * This component renders as pure HTML (not inside SVG foreignObject) to avoid
 * Safari's WebKit bug #23113 where HTML content with CSS transforms/opacity
 * inside foreignObject renders at incorrect positions.
 *
 * The parent PieChart uses CSS Grid stacking to overlay this HTML content
 * on top of the SVG slices.
 */
export function PieCenter(t0) {
  const $ = _c(38);
  const {
    defaultLabel: t1,
    formatOptions: t2,
    children,
    className: t3,
    valueClassName: t4,
    labelClassName: t5,
    prefix,
    suffix
  } = t0;
  const defaultLabel = t1 === undefined ? "Total" : t1;
  const formatOptions = t2 === undefined ? defaultFormatOptions : t2;
  const className = t3 === undefined ? "" : t3;
  const valueClassName = t4 === undefined ? "text-2xl font-bold" : t4;
  const labelClassName = t5 === undefined ? "text-xs" : t5;
  const {
    data,
    hoveredIndex,
    totalValue,
    innerRadius
  } = usePie();
  const hoveredData = hoveredIndex !== null ? data[hoveredIndex] : null;
  const displayValue = hoveredData ? hoveredData.value : totalValue;
  const displayLabel = hoveredData ? hoveredData.label : defaultLabel;
  const isHovered = hoveredIndex !== null;
  const centerSize = innerRadius * 2 - 16;
  if (innerRadius <= 0) {
    return null;
  }
  if (children && hoveredData) {
    let t6;
    if ($[0] !== className) {
      t6 = cn("flex items-center justify-center", className);
      $[0] = className;
      $[1] = t6;
    } else {
      t6 = $[1];
    }
    let t7;
    if ($[2] !== centerSize) {
      t7 = {
        width: centerSize,
        height: centerSize
      };
      $[2] = centerSize;
      $[3] = t7;
    } else {
      t7 = $[3];
    }
    let t8;
    if ($[4] !== children || $[5] !== displayLabel || $[6] !== displayValue || $[7] !== hoveredData || $[8] !== isHovered) {
      t8 = children({
        value: displayValue,
        label: displayLabel,
        isHovered,
        data: hoveredData
      });
      $[4] = children;
      $[5] = displayLabel;
      $[6] = displayValue;
      $[7] = hoveredData;
      $[8] = isHovered;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
      t9 = <div className={t6} style={t7}>{t8}</div>;
      $[10] = t6;
      $[11] = t7;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  let t6;
  if ($[14] !== className) {
    t6 = cn("flex flex-col items-center justify-center text-center", className);
    $[14] = className;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== centerSize) {
    t7 = {
      width: centerSize,
      height: centerSize
    };
    $[16] = centerSize;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== valueClassName) {
    t8 = cn("text-foreground tabular-nums", valueClassName);
    $[18] = valueClassName;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== displayValue || $[21] !== formatOptions || $[22] !== prefix || $[23] !== suffix) {
    t9 = <NumberFlow format={formatOptions} prefix={prefix} suffix={suffix} value={displayValue} willChange={true} />;
    $[20] = displayValue;
    $[21] = formatOptions;
    $[22] = prefix;
    $[23] = suffix;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <span className={t8}>{t9}</span>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== labelClassName) {
    t11 = cn("text-chart-label mt-0.5", labelClassName);
    $[28] = labelClassName;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== displayLabel || $[31] !== t11) {
    t12 = <span className={t11}>{displayLabel}</span>;
    $[30] = displayLabel;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t10 || $[34] !== t12 || $[35] !== t6 || $[36] !== t7) {
    t13 = <div className={t6} style={t7}>{t10}{t12}</div>;
    $[33] = t10;
    $[34] = t12;
    $[35] = t6;
    $[36] = t7;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}
PieCenter.displayName = "PieCenter";
export default PieCenter;